import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import type { MemberRole } from "./useAuth";

export type InstanceMemberStatus = "active" | "invited";

export type InstanceMember = {
  email: string;
  role: MemberRole;
  status: InstanceMemberStatus;
  invitedAt: string | null;
  invitedBy: string | null;
};

export type InstanceMembersResponse = {
  tenantId: string;
  ownerEmail: string;
  callerRole: MemberRole;
  memberCap: number;
  members: InstanceMember[];
};

export type InstanceMembersStatus = "loading" | "ready" | "error";

export type InstanceMembersErrorCode =
  | "DUPLICATE"
  | "LIMIT"
  | "OWNER_PROTECTED"
  | "SELF"
  | "FORBIDDEN"
  | "FEATURE_DISABLED"
  | "INVALID"
  | "NOT_FOUND"
  | "UNAUTHORIZED"
  | "UNKNOWN";

export type InstanceMembersResult<T> =
  | { ok: true; value: T }
  | { ok: false; code: InstanceMembersErrorCode; message: string };

export type UseInstanceMembersResult = {
  status: InstanceMembersStatus;
  team: InstanceMembersResponse | null;
  error: string | null;
  inviteMember: (email: string) => Promise<InstanceMembersResult<InstanceMember>>;
  removeMember: (email: string) => Promise<InstanceMembersResult<void>>;
  refresh: () => Promise<void>;
};

type ServerError = { code: string; message: string };

const ERROR_CODES: ReadonlyArray<InstanceMembersErrorCode> = [
  "DUPLICATE", "LIMIT", "OWNER_PROTECTED", "SELF", "FORBIDDEN",
  "FEATURE_DISABLED", "INVALID", "NOT_FOUND", "UNAUTHORIZED", "UNKNOWN",
];

const narrowCode = (code: string): InstanceMembersErrorCode =>
  ERROR_CODES.includes(code as InstanceMembersErrorCode)
    ? (code as InstanceMembersErrorCode)
    : "UNKNOWN";

const errorResult = async (
  res: Response,
  fallback: string,
): Promise<{ ok: false; code: InstanceMembersErrorCode; message: string }> => {
  if (res.status === 401) {
    return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
  }
  const body = (await res.json().catch(() => null)) as ServerError | null;
  return {
    ok: false,
    code: narrowCode(body?.code ?? "UNKNOWN"),
    message: body?.message ?? `${fallback} (${res.status})`,
  };
};

export const useInstanceMembers = (tenantId: string): UseInstanceMembersResult => {
  const [status, setStatus] = useState<InstanceMembersStatus>("loading");
  const [team, setTeam] = useState<InstanceMembersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const baseUrl = `/api/claws/${encodeURIComponent(tenantId)}/members`;

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch(baseUrl, { credentials: "include" });
      if (res.status === 200) {
        const body = (await res.json()) as InstanceMembersResponse;
        setTeam(body);
        setStatus("ready");
        setError(null);
      } else {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        setStatus("error");
        setError(body?.message ?? `Couldn't load collaborators (${res.status})`);
      }
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [baseUrl]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const inviteMember = useCallback(
    async (email: string): Promise<InstanceMembersResult<InstanceMember>> => {
      try {
        const res = await fetch(baseUrl, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ email }),
        });
        if (res.status === 201) {
          const member = (await res.json()) as InstanceMember;
          await refresh();
          return { ok: true, value: member };
        }
        return await errorResult(res, "Invite failed");
      } catch (err) {
        return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
      }
    },
    [baseUrl, refresh],
  );

  const removeMember = useCallback(
    async (email: string): Promise<InstanceMembersResult<void>> => {
      try {
        const res = await fetch(`${baseUrl}/${encodeURIComponent(email)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.status === 204) {
          await refresh();
          return { ok: true, value: undefined };
        }
        return await errorResult(res, "Remove failed");
      } catch (err) {
        return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
      }
    },
    [baseUrl, refresh],
  );

  return { status, team, error, inviteMember, removeMember, refresh };
};
